/*
 * Every colour, space, radius, font and shadow here is a design token.
 * The only literals are structural hairlines (1px borders) and `transparent`.
 * Keep it that way — a lifted copy of this file stays on-brand only because it
 * resolves var(--…) from @parrot/tokens.
 */

.base,
.base *,
.base::before,
.base::after,
.base *::before,
.base *::after {
  box-sizing: border-box;
}

.base {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);

  font-family: var(--font-sans);
  font-weight: var(--weight-semi);
  line-height: var(--leading-tight);
  white-space: nowrap;
  text-decoration: none;

  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;

  transition:
    background-color var(--duration-fast) var(--ease-default),
    border-color var(--duration-fast) var(--ease-default),
    box-shadow var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
}

/* An outline, not a box-shadow. Shadow was wrong here — .glass carries its own
   box-shadow and focus overwrote it, so focusing a glass button flattened it.
   Outline draws on its own layer and can't collide. */
.base:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.base:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  box-shadow: none;
}

.base.loading:disabled {
  cursor: wait;
  opacity: 1;
}

/* ── Sizes ── */

.sm {
  height: var(--control-height-sm);
  padding-inline: var(--space-3);
  font-size: var(--text-sm);
}

.md {
  height: var(--control-height-md);
  padding-inline: var(--space-4);
  font-size: var(--text-base);
}

.lg {
  height: var(--control-height-lg);
  padding-inline: var(--space-6);
  font-size: var(--text-md);
}

/* Two classes, so it beats the size rule's padding whatever the source order.
   aspect-ratio against the size height gives 30/36/42px square with no new
   constants. */
.base.iconOnly {
  padding-inline: 0;
  aspect-ratio: 1;
}

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Relative to the button's font-size, so one rule covers all three sizes. */
.icon > svg {
  width: 1.15em;
  height: 1.15em;
}

/* ── Variants ── */

.primary {
  background-image: linear-gradient(180deg, var(--btn-primary-from), var(--btn-primary-to));
  color: var(--btn-primary-text);
  box-shadow: var(--btn-primary-glow);
}

.primary:hover:not(:disabled) {
  box-shadow: var(--btn-primary-glow-hover);
}

.destructive {
  background-image: linear-gradient(180deg, var(--btn-dest-from), var(--btn-dest-to));
  color: var(--btn-dest-text);
  box-shadow: var(--btn-dest-glow);
}

.destructive:hover:not(:disabled) {
  box-shadow: var(--btn-dest-glow-hover);
}

.brand {
  background-image: linear-gradient(180deg, var(--btn-gold-from), var(--btn-gold-to));
  color: var(--btn-gold-text);
  box-shadow: var(--btn-gold-glow);
}

.brand:hover:not(:disabled) {
  box-shadow: var(--btn-gold-glow-hover);
}

.secondary {
  background-color: var(--bg-elevated);
  color: var(--text-primary);
  border-color: var(--border-default);
}

.secondary:hover:not(:disabled) {
  background-color: var(--bg-hover);
  border-color: var(--border-strong);
}

.secondary:active:not(:disabled) {
  background-color: var(--bg-active);
}

.ghost {
  background-color: transparent;
  color: var(--text-secondary);
}

.ghost:hover:not(:disabled) {
  background-color: var(--bg-hover);
  color: var(--text-primary);
}

.ghost:active:not(:disabled) {
  background-color: var(--bg-active);
}

/* Glass tints the backdrop rather than covering it, so it only reads over
   something with content behind it — a gradient, an image, a busy surface. */
.glass {
  position: relative;
  background-color: var(--btn-glass-bg);
  backdrop-filter: blur(var(--btn-glass-blur));
  -webkit-backdrop-filter: blur(var(--btn-glass-blur));
  color: var(--text-primary);
  box-shadow: var(--btn-glass-shadow);
}

/* The edge is a masked gradient rather than a border, so the 1px ring can fade
   along its length. border-box + inset 0 keeps it exactly on the button's edge. */
.glass::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: inherit;
  background: var(--btn-glass-stroke);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  pointer-events: none;
}

.glass:hover:not(:disabled) {
  background-color: var(--btn-glass-bg-hover);
  box-shadow: var(--btn-glass-shadow-hover);
}

.glass:hover:not(:disabled)::before {
  background: var(--btn-glass-stroke-hover);
}

.glass:active:not(:disabled) {
  background-color: var(--btn-glass-bg-active);
}

/* Without the blur an 8% white fill is all but invisible, so fall back to a solid
   surface. Safari shipped this prefixed only, hence testing both. */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .glass {
    background-color: var(--btn-glass-bg-opaque);
  }
}

/* The gradient variants carry their glow as box-shadow, so the pressed state
   reads through the ring token rather than a background change. */
.primary:active:not(:disabled) {
  box-shadow: var(--btn-primary-glow-active);
}

.destructive:active:not(:disabled) {
  box-shadow: var(--btn-dest-glow-active);
}

.brand:active:not(:disabled) {
  box-shadow: var(--btn-gold-glow-active);
}

@media (prefers-reduced-motion: reduce) {
  .base {
    transition: none;
  }
}
